import { useState, useEffect } from 'react';
import { fetchWithAuth } from '../utils/api';

export interface TaxonomyDimension {
  key: string;
  display_name: string;
  color: string;
  order: number;
  description?: string;
}

export interface TaxonomySchema {
  version: string;
  dimensions: TaxonomyDimension[];
  updated_at: string;
}

// Define color classes that Tailwind will always include
// These need to be static strings for Tailwind's purge to work
const DIMENSION_COLORS = {
  dissatisfaction: 'bg-blue-100 text-blue-900',
  complaint_detection: 'bg-blue-100 text-blue-900', // Alias for dissatisfaction (taxonomy v6.0)
  vulnerability: 'bg-purple-100 text-purple-900',
  customer_strain: 'bg-orange-100 text-orange-900',
  foreseeable_harm: 'bg-red-100 text-red-900',
  root_causes: 'bg-indigo-100 text-indigo-900',
  consumer_duty: 'bg-teal-100 text-teal-900',
  outcomes_and_cross_cutting: 'bg-teal-100 text-teal-900' // Alias for older taxonomy versions
};

// Dimension ID aliases for backwards compatibility
// Maps old dimension IDs to their canonical form
const DIMENSION_ALIASES: Record<string, string> = {
  complaint_detection: 'dissatisfaction',
  dissatisfaction: 'dissatisfaction',
};

// Normalize dimension key to handle both old and new IDs
const normalizeDimensionKey = (key: string): string => {
  return DIMENSION_ALIASES[key] || key;
};

// Fallback dimensions based on current production taxonomy
const FALLBACK_DIMENSIONS: TaxonomyDimension[] = [
  {
    key: 'dissatisfaction',
    display_name: 'Dissatisfaction',
    color: DIMENSION_COLORS.dissatisfaction,
    order: 1
  },
  {
    key: 'vulnerability',  // This was missing!
    display_name: 'Vulnerability',
    color: DIMENSION_COLORS.vulnerability, 
    order: 2
  },
  {
    key: 'customer_strain',
    display_name: 'Customer Strain',
    color: DIMENSION_COLORS.customer_strain,
    order: 3
  },
  {
    key: 'foreseeable_harm',
    display_name: 'Foreseeable Harm',
    color: DIMENSION_COLORS.foreseeable_harm,
    order: 4
  },
  {
    key: 'root_causes',
    display_name: 'Root Causes',
    color: DIMENSION_COLORS.root_causes,
    order: 5
  },
  {
    key: 'consumer_duty',
    display_name: 'Consumer Duty',
    color: DIMENSION_COLORS.consumer_duty,
    order: 6
  }
];

export const useTaxonomySchema = (taxonomyVersion?: string) => {
  const [schema, setSchema] = useState<TaxonomySchema | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSchema = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Try to fetch from API
        const endpoint = taxonomyVersion 
          ? `/v1/taxonomy/schema?version=${encodeURIComponent(taxonomyVersion)}`
          : '/v1/taxonomy/schema';
          
        const response = await fetchWithAuth(endpoint);
        
        if (response && response.dimensions) {
          setSchema(response);
        } else {
          // Use fallback if API doesn't have schema endpoint yet
          setSchema({
            version: taxonomyVersion || 'default',
            dimensions: FALLBACK_DIMENSIONS,
            updated_at: new Date().toISOString()
          });
        }
      } catch (err) {
        console.warn('Failed to fetch taxonomy schema, using fallback:', err);
        // Use fallback dimensions if API fails
        setSchema({
          version: taxonomyVersion || 'default',
          dimensions: FALLBACK_DIMENSIONS,
          updated_at: new Date().toISOString()
        });
      } finally {
        setLoading(false);
      }
    };

    fetchSchema();
  }, [taxonomyVersion]);

  // Helper functions
  const getDimensionColor = (dimensionKey: string): string => {
    // First try to get color from schema
    const dimension = schema?.dimensions.find(d => d.key === dimensionKey);
    if (dimension?.color) {
      // If the color from API looks valid, use it
      if (dimension.color.includes('bg-') && dimension.color.includes('text-')) {
        return dimension.color;
      }
    }
    
    // Fallback to our static color mapping to ensure Tailwind includes these classes
    const colorKey = dimensionKey as keyof typeof DIMENSION_COLORS;
    return DIMENSION_COLORS[colorKey] || 'bg-gray-100 text-gray-900';
  };

  const getDimensionDisplayName = (dimensionKey: string): string => {
    const dimension = schema?.dimensions.find(d => d.key === dimensionKey);
    return dimension?.display_name || dimensionKey.split('_').map(
      word => word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const getSortedDimensions = (): TaxonomyDimension[] => {
    if (!schema) return FALLBACK_DIMENSIONS;
    return [...schema.dimensions].sort((a, b) => a.order - b.order);
  };

  return {
    schema,
    dimensions: schema?.dimensions || FALLBACK_DIMENSIONS,
    loading,
    error,
    getDimensionColor,
    getDimensionDisplayName,
    getSortedDimensions,
    normalizeDimensionKey
  };
};

// Export the normalize function for use outside the hook
export { normalizeDimensionKey };